import { toggleMark } from 'prosemirror-commands';
import { Fragment } from 'prosemirror-model';
import { EditorState, TextSelection } from 'prosemirror-state';
import { undo, redo } from 'prosemirror-history';
import { wrapInList, liftListItem } from 'prosemirror-schema-list';

/**
 * Um mark esta ativo quando cobre a selecao inteira, ou — com o cursor parado —
 * quando ele entraria no proximo caractere digitado. Sem o segundo caso, apertar
 * Ctrl+B com o cursor parado nao acenderia o botao, e o usuario nao teria como
 * saber que o negrito esta armado.
 *
 * @param {EditorState} state
 * @param {MarkType} markType
 * @returns {boolean}
 */
export function isMarkActive(state, markType) {
  if (!markType) return false;

  const { from, $from, to, empty } = state.selection;

  if (empty) {
    return !!markType.isInSet(state.storedMarks || $from.marks());
  }

  return state.doc.rangeHasMark(from, to, markType);
}

/**
 * Descobre a extensao completa de um mark ao redor de uma posicao.
 *
 * Necessario porque `toggleMark` com a selecao vazia so mexe nos stored marks
 * — o texto ja marcado fica intacto. Para remover ou reescrever o link em que
 * o cursor esta parado, e preciso saber onde ele comeca e termina.
 *
 * @param {ResolvedPos} $pos
 * @param {MarkType} markType
 * @returns {{from: number, to: number, mark: Mark}|null}
 */
export function findMarkRange($pos, markType) {
  if (!markType || !$pos.parent.childCount) return null;

  let start = $pos.parent.childAfter($pos.parentOffset);

  // Com o cursor encostado no fim do trecho marcado, `childAfter` devolve o no
  // seguinte, que nao carrega o mark; nesse caso olhamos para tras.
  if (!start.node || !markType.isInSet(start.node.marks)) {
    start = $pos.parent.childBefore($pos.parentOffset);
  }

  if (!start.node || !markType.isInSet(start.node.marks)) return null;

  const mark = markType.isInSet(start.node.marks);
  let startIndex = start.index;
  let startPos = $pos.start() + start.offset;
  let endIndex = startIndex + 1;
  let endPos = startPos + start.node.nodeSize;

  // O texto marcado pode estar quebrado em varios nos (por outros marks, por
  // exemplo); expande para os dois lados enquanto o mesmo mark continuar.
  while (
    startIndex > 0 &&
    mark.isInSet($pos.parent.child(startIndex - 1).marks)
  ) {
    startIndex -= 1;
    startPos -= $pos.parent.child(startIndex).nodeSize;
  }

  while (
    endIndex < $pos.parent.childCount &&
    mark.isInSet($pos.parent.child(endIndex).marks)
  ) {
    endPos += $pos.parent.child(endIndex).nodeSize;
    endIndex += 1;
  }

  return { from: startPos, to: endPos, mark };
}

/**
 * Extensao do link na selecao atual: o trecho selecionado, se houver, ou o
 * link inteiro em que o cursor esta parado.
 *
 * @param {EditorState} state
 * @returns {{from: number, to: number, mark: Mark}|null}
 */
export function findLinkRange(state) {
  const markType = state.schema.marks.link;
  if (!markType) return null;

  const { $from, from, to, empty } = state.selection;

  // Com selecao, a ausencia do mark na faixa selecionada ja e resposta: nao ha
  // por que olhar para os vizinhos. O `findMarkRange` faz `childBefore` quando o
  // no sob a posicao nao carrega o mark, e o inicio de uma selecao que comeca
  // exatamente onde um link termina cai nessa fronteira — ele devolveria a faixa
  // do link ANTERIOR, e o `toggleLink` apagaria esse link em vez de criar um novo.
  if (!empty) {
    if (!state.doc.rangeHasMark(from, to, markType)) return null;

    return findMarkRange($from, markType) || { from, to, mark: null };
  }

  return findMarkRange($from, markType);
}

/**
 * Verifica se a selecao esta dentro de um no de determinado tipo (lista, por
 * exemplo), subindo pela arvore a partir do cursor.
 *
 * @param {EditorState} state
 * @param {NodeType} nodeType
 * @returns {boolean}
 */
export function isNodeActive(state, nodeType) {
  if (!nodeType) return false;

  const { $from } = state.selection;

  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if ($from.node(depth).type === nodeType) {
      return true;
    }
  }

  return false;
}

/**
 * Pontas da selecao trazidas para dentro de um textblock.
 *
 * `Ctrl+A` esta no `baseKeymap` (`Mod-a` -> `selectAll`) e produz uma
 * `AllSelection`, cujas pontas caem no proprio `doc` — `from` e `0`, que nao e
 * posicao de texto. `between` desce cada uma ate a posicao de texto mais
 * proxima dentro dela.
 *
 * Sem isso o corte abaixo desiste na guarda de textblock, `docChanged` fica
 * falso e a lista volta a envolver o paragrafo inteiro num item so — o defeito
 * que `splitSelectedLines` existe para resolver, sobrevivendo justamente no
 * caminho mais curto para "transformar tudo isso em lista".
 *
 * @param {EditorState} state
 * @returns {{from: number, to: number}}
 */
function textRange(state) {
  const { from, to } = TextSelection.between(
    state.doc.resolve(state.selection.from),
    state.doc.resolve(state.selection.to)
  );

  return { from, to };
}

/**
 * Promove a bloco as quebras `hard_break` que delimitam as linhas selecionadas.
 *
 * Para o ProseMirror, linhas separadas por Shift+Enter sao UM paragrafo so: o
 * `hard_break` e conteudo dele, nao fronteira. Por isso `wrapInList` envolvia o
 * paragrafo inteiro num unico item — pedir lista para a ultima linha punha o
 * marcador na primeira e indentava todas as outras junto, que era o relato de
 * "apliquei embaixo e foi para o topo".
 *
 * Aqui as quebras que cercam a selecao viram fronteira de verdade, e cada linha
 * selecionada pode virar um item. As demais quebras ficam como estao, para o
 * corte nao passar do que o usuario pediu.
 *
 * MUDA a mensagem entregue, e de proposito. A fronteira de bloco que o corte
 * cria vale uma linha em branco — `texto\n\n- a\n\nfim` —, enquanto a quebra
 * valia so uma quebra simples. E o que a lista significa: no WhatsApp ela vem
 * separada do texto em volta. O editor desenha o mesmo vao (ver a margem de
 * `ul`/`ol` no `RichTextEditor.vue`), entao tela e mensagem continuam batendo.
 *
 * O comentario antigo dizia o contrario, citando um `collapseBlockBreaks` que
 * juntava tudo na entrega. Essa funcao deixou de existir: hoje a linha em
 * branco entre blocos e conteudo e chega ao contato.
 *
 * @param {EditorState} state
 * @param {Transaction} tr transacao onde os cortes sao acumulados
 * @returns {Transaction} a mesma transacao
 */
function splitSelectedLines(state, tr) {
  const br = state.schema.nodes.hard_break;
  if (!br) return tr;

  const { from, to } = textRange(state);
  const $from = state.doc.resolve(from);
  const $to = state.doc.resolve(to);

  if (!$from.parent.isTextblock || !$to.parent.isTextblock) return tr;

  const quebras = [];
  state.doc.nodesBetween($from.start(), $to.end(), (node, pos) => {
    if (node.type === br) quebras.push(pos);
  });

  // As de dentro separam uma linha selecionada da seguinte; as das pontas
  // destacam o trecho do resto do paragrafo. Com o cursor parado nao ha
  // nenhuma de dentro, e sao so as pontas que isolam a linha do cursor.
  const alvos = quebras.filter(pos => pos >= from && pos < to);
  const anterior = quebras.filter(pos => pos < from).pop();
  const posterior = quebras.find(pos => pos >= to);

  if (anterior !== undefined) alvos.push(anterior);
  if (posterior !== undefined) alvos.push(posterior);

  // De tras para frente: cortar no inicio deslocaria as posicoes seguintes.
  [...new Set(alvos)]
    .sort((a, b) => b - a)
    .forEach(pos => {
      tr.delete(pos, pos + 1);
      tr.split(pos);
    });

  return tr;
}

/**
 * Liga/desliga uma lista. Ligar envolve o bloco; desligar sobe o item para
 * fora da lista.
 *
 * @param {NodeType} nodeType tipo da lista
 * @returns {Function} comando ProseMirror
 */
export function toggleList(nodeType) {
  return (state, dispatch) => {
    if (isNodeActive(state, nodeType)) {
      // Soltar o item deixa dois paragrafos colados, e o `blankLinePlugin` os
      // junta com o par de sempre — a lista some e as linhas em branco que a
      // fronteira dela representava ficam. Ver `parDeQuebras` para o porque de
      // nao ser uma quebra so.
      return liftListItem(state.schema.nodes.list_item)(state, dispatch);
    }

    const tr = splitSelectedLines(state, state.tr);

    if (!tr.docChanged) return wrapInList(nodeType)(state, dispatch);

    // A selecao precisa reapontar para as mesmas linhas depois do corte, senao
    // `wrapInList` agiria sobre outro trecho do documento. As pontas saem de
    // `textRange` pelo mesmo motivo do corte: as do `Ctrl+A` nao sao posicoes
    // de texto, e `TextSelection.create` sobre elas nao aponta para linha
    // nenhuma.
    const { from, to } = textRange(state);

    // Os biases opostos encolhem a selecao para dentro das linhas escolhidas —
    // certo quando ha trecho selecionado, e errado quando o cursor esta parado.
    // Com `from === to` em cima da fronteira que o corte acabou de criar, as
    // duas pontas precisam do MESMO bias: com biases opostos um cursor virava
    // intervalo e a lista pegava tambem a linha vizinha.
    //
    // Qual bias depende de onde o cursor estava ANTES do corte: encostado na
    // quebra ele marca o FIM da linha atual (-1, fica no paragrafo de cima); em
    // qualquer outro ponto ele pertence a linha que comeca ali (1). Sem essa
    // distincao, terminar de escrever uma linha e clicar no botao punha o
    // marcador na linha de baixo — que e o gesto mais comum de todos.
    const cursorParado = from === to;
    const noFimDaLinha =
      state.doc.resolve(to).nodeAfter?.type === state.schema.nodes.hard_break;
    const biasInicial = cursorParado && noFimDaLinha ? -1 : 1;
    const biasFinal = cursorParado ? biasInicial : -1;

    tr.setSelection(
      TextSelection.create(
        tr.doc,
        tr.mapping.map(from, biasInicial),
        tr.mapping.map(to, biasFinal)
      )
    );

    // O estado intermediario e montado SEM plugin nenhum.
    //
    // `state.apply` roda o `appendTransaction` de todos eles, e o
    // `blankLinePlugin` junta de volta os paragrafos que `splitSelectedLines`
    // acabou de separar — o `wrapInList` recebia um paragrafo so e engolia o
    // texto inteiro para dentro do item. Aqui ele so precisa de documento,
    // selecao e schema.
    const intermediario = EditorState.create({
      doc: tr.doc,
      selection: tr.selection,
      schema: state.schema,
    });

    // Os passos da lista entram na MESMA transacao: dois dispatches virariam
    // dois passos de desfazer, e o Ctrl+Z deixaria os paragrafos partidos.
    let aplicou = false;
    wrapInList(nodeType)(intermediario, trLista => {
      trLista.steps.forEach(step => tr.step(step));
      aplicou = true;
    });

    if (!aplicou) return false;
    if (dispatch) dispatch(tr.scrollIntoView());

    return true;
  };
}

/**
 * A selecao ocupa mais de uma linha?
 *
 * Contar textblocks nao basta: duas linhas separadas por Shift+Enter sao um
 * paragrafo so, com um `hard_break` no meio.
 *
 * Decide tanto a forma aplicada — crase simples ou bloco — quanto o nome que a
 * barra exibe antes de aplicar.
 *
 * @param {EditorState} state
 * @returns {boolean}
 */
export function selectionSpansLines(state) {
  const { from, to } = state.selection;
  let blocos = 0;
  let quebras = 0;

  state.doc.nodesBetween(from, to, node => {
    if (node.isTextblock) blocos += 1;
    if (node.type === state.schema.nodes.hard_break) quebras += 1;
  });

  return blocos > 1 || quebras > 0;
}

/**
 * Devolve o fragmento sem a quebra de linha encostada na borda indicada.
 *
 * O que sobra de fora do bloco de codigo termina — ou comeca — na quebra que
 * separava a linha convertida da vizinha. Essa quebra nao separa mais nada, e
 * ficaria como uma linha em branco colada no bloco.
 *
 * @param {Fragment} fragment
 * @param {NodeType} hardBreak
 * @param {'inicio'|'fim'} borda
 * @returns {Fragment}
 */
function withoutEdgeBreak(fragment, hardBreak, borda) {
  const no = borda === 'inicio' ? fragment.firstChild : fragment.lastChild;

  if (!no || no.type !== hardBreak) return fragment;

  return borda === 'inicio'
    ? fragment.cut(no.nodeSize)
    : fragment.cut(0, fragment.size - no.nodeSize);
}

/**
 * Monta a troca que poe o bloco de codigo no lugar das linhas selecionadas.
 *
 * O bloco ocupa linhas inteiras, entao a troca acontece sobre os blocos que a
 * selecao toca, e nao sobre o intervalo cru. Com as posicoes cruas o
 * ProseMirror parte o paragrafo para acomodar o bloco e deixa para tras a
 * casca do que sobrou — a linha em branco que aparecia acima do monoespacado.
 *
 * O texto de fora da selecao volta como paragrafo, antes e depois do bloco,
 * com as marcacoes que tinha.
 *
 * @param {EditorState} state
 * @param {Node} bloco o `code_block` ja montado
 * @returns {{from: number, to: number, nodes: Node[], blockPos: number}|null}
 *   null quando as pontas ficam em niveis diferentes, ou quando o pai nao
 *   aceita a troca — item de lista cuja primeira posicao entra na selecao, por
 *   exemplo, onde o `code_block` cairia antes do paragrafo obrigatorio.
 */
export function codeBlockReplacement(state, bloco) {
  const { $from, $to } = state.selection;
  const { paragraph, hard_break: hardBreak } = state.schema.nodes;
  const range = $from.blockRange($to);

  if (!range) return null;

  // Pontas em niveis diferentes (uma dentro de uma citacao e a outra fora):
  // o que sobra de cada lado nao e simplesmente um paragrafo.
  if ($from.depth !== range.depth + 1 || $to.depth !== range.depth + 1) {
    return null;
  }

  const antes = withoutEdgeBreak(
    $from.parent.content.cut(0, $from.parentOffset),
    hardBreak,
    'fim'
  );
  const depois = withoutEdgeBreak(
    $to.parent.content.cut($to.parentOffset),
    hardBreak,
    'inicio'
  );

  const nodes = [
    ...(antes.size ? [paragraph.create(null, antes)] : []),
    bloco,
    ...(depois.size ? [paragraph.create(null, depois)] : []),
  ];

  const cabe = range.parent.canReplace(
    range.startIndex,
    range.endIndex,
    Fragment.from(nodes)
  );

  if (!cabe) return null;

  return {
    from: range.start,
    to: range.end,
    nodes,
    blockPos: range.start + (antes.size ? nodes[0].nodeSize : 0),
  };
}

/**
 * Resolve a ferramenta da barra no comando correspondente.
 *
 * @param {Object} tool entrada de EDITOR_TOOLS
 * @param {Schema} schema
 * @returns {Function|null} comando ProseMirror
 */
export function commandForTool(tool, schema) {
  if (tool.kind === 'mark') {
    const markType = schema.marks[tool.mark];
    return markType ? toggleMark(markType) : null;
  }

  if (tool.kind === 'node') {
    const nodeType = schema.nodes[tool.node];
    return nodeType ? toggleList(nodeType) : null;
  }

  if (tool.action === 'undo') return undo;
  if (tool.action === 'redo') return redo;

  // `link` abre interface propria e e tratado no componente.
  return null;
}

/**
 * Indica se a ferramenta esta ativa na selecao atual.
 *
 * @param {Object} tool entrada de EDITOR_TOOLS
 * @param {EditorState} state
 * @returns {boolean}
 */
export function isToolActive(tool, state) {
  // Codigo esta ativo nas duas formas: mark inline ou bloco.
  if (tool.action === 'code') {
    return (
      isMarkActive(state, state.schema.marks.code) ||
      isNodeActive(state, state.schema.nodes.code_block)
    );
  }

  if (tool.kind === 'mark') {
    return isMarkActive(state, state.schema.marks[tool.mark]);
  }

  if (tool.kind === 'node') {
    return isNodeActive(state, state.schema.nodes[tool.node]);
  }

  if (tool.action === 'link') {
    return isMarkActive(state, state.schema.marks.link);
  }

  return false;
}

/**
 * Indica se a ferramenta pode ser acionada agora. Desfazer/refazer ficam
 * apagados quando nao ha historico, e o link exige texto selecionado.
 *
 * @param {Object} tool entrada de EDITOR_TOOLS
 * @param {EditorState} state
 * @returns {boolean}
 */
export function isToolEnabled(tool, state) {
  if (tool.action === 'undo') return undo(state);
  if (tool.action === 'redo') return redo(state);

  // Um documento vazio ainda tem o paragrafo que o envolve, de tamanho 2.
  if (tool.action === 'clear') return state.doc.content.size > 2;

  if (tool.action === 'code') return !!state.schema.marks.code;

  // Inserir variavel vale sempre: o botao so existe onde `enableVariables`
  // esta ligado, e ali nao ha estado do documento que o impeca. Sem este caso
  // ele cairia no commandForTool, que devolve null para acao desconhecida, e o
  // botao nasceria permanentemente desabilitado.
  if (tool.action === 'variable') return true;

  if (tool.action === 'link') {
    return (
      !state.selection.empty || isMarkActive(state, state.schema.marks.link)
    );
  }

  const command = commandForTool(tool, state.schema);
  return command ? command(state) : false;
}
